/**
 * Begin Again Guide — Frontend analytics helper.
 *
 * Privacy contract:
 * - Anonymous UUID stored in sessionStorage (cleared when the tab closes).
 * - Fires categorical events only. Never sends free-text visitor messages,
 *   names, emails, phone numbers, or IP-like data.
 * - Uses fetch with `keepalive:true` so events survive page navigation but
 *   never block the UI. All failures are swallowed — analytics must never
 *   affect the visitor's experience.
 */

const SESSION_KEY = "bag_session_id";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function makeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: reasonably-random 32-char id.
  return `bag-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function getSessionId() {
  const store = safeStorage();
  if (!store) return makeId(); // ephemeral if sessionStorage is unavailable
  let id = store.getItem(SESSION_KEY);
  if (!id) {
    id = makeId();
    try {
      store.setItem(SESSION_KEY, id);
    } catch {
      /* ignore */
    }
  }
  return id;
}

function detectDevice() {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth || 0;
  const ua = (navigator.userAgent || "").toLowerCase();
  const isTablet = /ipad|tablet|playbook|silk/.test(ua) || (w >= 640 && w < 1024 && /mobile/.test(ua));
  if (isTablet) return "tablet";
  if (/mobi|iphone|ipod|android|blackberry|opera mini|iemobile/.test(ua) || w < 640) return "mobile";
  return "desktop";
}

function currentPagePath() {
  if (typeof window === "undefined") return null;
  const p = window.location.pathname || "/";
  // strip query + hash defensively
  return p.split("?")[0].split("#")[0].slice(0, 128);
}

/**
 * Sanitize the payload one more time on the client. The server also enforces
 * this — but layered defense keeps accidental PII from ever leaving the
 * browser.
 */
const ALLOWED_KEYS = new Set([
  "event_type",
  "anon_session_id",
  "pathway",
  "option_id",
  "target_route",
  "chip",
  "device",
  "page_path",
  "country",
]);

function clean(obj) {
  const out = {};
  for (const k of Object.keys(obj || {})) {
    if (!ALLOWED_KEYS.has(k)) continue;
    const v = obj[k];
    if (v === undefined || v === null) continue;
    out[k] = typeof v === "string" ? v.slice(0, 128) : v;
  }
  return out;
}

/** Fire-and-forget event send. Returns immediately. */
export function track(event_type, extras = {}) {
  try {
    const body = clean({
      event_type,
      anon_session_id: getSessionId(),
      device: detectDevice(),
      page_path: currentPagePath(),
      ...extras,
    });
    // Prefer fetch keepalive so events survive tab-close / route-change.
    fetch(`${API_BASE}/guide/events`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch(() => {
      /* swallow — analytics must never break the UX */
    });
  } catch {
    /* swallow */
  }
}

// Convenience wrappers used by the Guide widget.
export const analytics = {
  open: () => track("guide_open"),
  primaryPathway: (pathway) => track("primary_pathway_select", { pathway }),
  subOption: (pathway, option_id) => track("sub_option_select", { pathway, option_id }),
  humanChip: (chip) => track("human_chip_click", { chip }),
  routeClick: (pathway, target_route) => track("route_click_through", { pathway, target_route }),
  externalClick: (pathway, target_route) => track("external_link_click", { pathway, target_route }),
  freeTextSubmit: (pathway) => track("free_text_submit", { pathway }),
  safetyTriggered: () => track("safety_pathway_triggered"),
  safetyCountry: (country) => track("safety_country_select", { country }),
  engaged: () => track("session_engaged"),
  warmWelcomeShown: () => track("warm_welcome_shown"),
  warmWelcomeOpened: () => track("warm_welcome_opened"),
  warmWelcomeDismissed: () => track("warm_welcome_dismissed"),
};

export function resetAnonSessionForTests() {
  const store = safeStorage();
  if (store) store.removeItem(SESSION_KEY);
}
